import React, { PureComponent } from 'react';

import { connect } from 'react-redux';
import VirtualizedList from '@enykeev/react-virtualized/dist/commonjs/List';
import ResizeSensor from 'resize-sensor';
import { getAllNestedTags, addFormTag, deleteTag, renameTag, moveTag } from './../actions';
import TagItem from './TagItem';
import Loader from '../../components/loader';
import { RootState } from 'gf-types';
import { TagsItem, TagsList, TagState } from './../tags-types';

export type TagListProps = {
  tags?: TagsItem[];
  getAllNestedTags?: () => void;
  tagsLoading?: boolean;
  tagsLoaded?: boolean;
  addFormTag?: () => void;
  search?: string;
  deleteTag?: () => void;
  renameTag?: () => void;
  moveTag?: () => void;
};

//@ts-ignore - FIX
@connect(
  (state: RootState) => ({
    tags: state.tags?.items,
    tagsLoading: state.tags?.loading,
    tagsLoaded: state.tags?.loaded
  }),
  { getAllNestedTags, addFormTag, deleteTag, renameTag, moveTag }
)
export default class TagList extends PureComponent<TagListProps, TagsList> {
  private resizeSensor: ResizeSensor;
  private tagList: TagsList;

  constructor(props: TagListProps) {
    super(props);

    this.toggleExpanded = this.toggleExpanded.bind(this);
    this.updateDimensions = this.updateDimensions.bind(this);

    this.state = {
      expandedItems: [],
      tags: this.props.tags,
      listWidth: 0,
      listHeight: 0
    };
  }

  componentDidMount() {
    if (!this.props.tagsLoaded && !this.props.tagsLoading) {
      this.props.getAllNestedTags();
    }

    this.resizeSensor = new ResizeSensor(this.tagList, this.updateDimensions);
  }

  componentDidUpdate(prevProps: TagListProps, prevState: TagState) {
    if (prevProps.tags !== this.props.tags || prevProps.search !== this.props.search) {
      if (this.props.search) {
        const result = this.getTagsBySearch(this.props.tags);
        this.setState({
          tags: result.filteredTags,
          expandedItems: result.expandedIds
        });
      } else {
        this.setState({ tags: this.props.tags, expandedItems: prevProps.search !== this.props.search ? [] : prevState.expandedItems });
      }
    }
  }

  componentWillUnmount() {
    if (this.resizeSensor) {
      this.resizeSensor.detach();
    }
  }

  getTagsBySearch(tags: TagsItem[]) {
    const filteredTags = [];
    let expandedIds = [];
    for (const tag of tags) {
      const c = this.getTagsBySearch(tag.children);
      const filteredChildren = c.filteredTags;
      if (filteredChildren.length > 0 || tag.name.toLowerCase().indexOf(this.props.search.toLowerCase()) > -1) {
        filteredTags.push({
          id: tag.id,
          name: tag.name,
          children: filteredChildren
        });
      }
      if (filteredChildren.length > 0) {
        expandedIds = [...expandedIds, tag.id, ...c.expandedIds];
      }
    }
    return { filteredTags: filteredTags, expandedIds };
  }

  getFlattenedTagList(tags: TagsItem[], level: number, parentIds: string[]) {
    let list = [];
    if (tags) {
      for (const tag of tags) {
        list.push({
          level,
          tag,
          parentIds
        });
        if (this.state.expandedItems.indexOf(tag.id) > -1) {
          list = [...list, ...this.getFlattenedTagList(tag.children, level + 1, [...parentIds, tag.id])];
        }
      }
    }
    return list;
  }

  updateDimensions() {
    this.setState({
      listWidth: this.tagList.offsetWidth,
      listHeight: this.tagList.offsetHeight
    });
  }

  toggleExpanded(id: string) {
    this.setState((prevState: TagState) => {
      let expandedItems;
      const currentIndex = prevState.expandedItems.indexOf(id);
      if (currentIndex > -1) {
        expandedItems = [...prevState.expandedItems];
        expandedItems.splice(currentIndex, 1);
      } else {
        expandedItems = [id, ...prevState.expandedItems];
      }
      return { expandedItems };
    });
  }

  render() {
    const { tagsLoading, tagsLoaded, search } = this.props;

    if (!tagsLoaded || tagsLoading) {
      return (
        <div
          key="tag-list"
          className="tag-list"
          ref={(r) => {
            this.tagList = r;
          }}
        >
          <Loader isLoading />
        </div>
      );
    }

    const flattenedList = this.getFlattenedTagList(this.state.tags, 0, []);

    const rowRenderer = (options) => {
      const { key, index, style } = options;
      const listItem = flattenedList[index];
      const { tag, level, parentIds } = listItem;
      return (
        <div key={key} style={style}>
          <TagItem
            tag={tag}
            selectTag={this.props.addFormTag}
            level={level}
            expandedItems={this.state.expandedItems}
            toggleExpanded={this.toggleExpanded}
            onRemove={this.props.deleteTag}
            onRename={this.props.renameTag}
            onMove={this.props.moveTag}
            parentIds={parentIds}
            parentId={parentIds.length > 0 ? parentIds[parentIds.length - 1] : null}
            highlight={search}
          />
        </div>
      );
    };

    return (
      <div
        key="tag-list"
        className="tag-list"
        ref={(r) => {
          this.tagList = r;
        }}
      >
        <VirtualizedList rowCount={flattenedList.length} height={this.state.listHeight} width={this.state.listWidth} rowHeight={32} rowRenderer={rowRenderer} />
      </div>
    );
  }
}
